import type { StrictSpacingStyle } from '../util/types';

import { Box } from '../Box';
import { Spinner } from '../Spinner';
import { Text } from '../Text';
import { useTheme } from '../ThemeProvider';
import { useFocusRing } from '../util/useFocusRing';

import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Pressable as RNPressable,
    type StyleProp,
    type TextStyle,
    View,
    type ViewStyle,
    type AccessibilityRole,
    type AccessibilityState,
    Platform,
} from 'react-native';

type PressableWebProps = React.ComponentPropsWithRef<typeof RNPressable> & {
    href?: string;
    hrefAttrs?: {
        download?: boolean;
        rel?: string;
        target?: string;
    };
};

const Pressable = RNPressable as React.ComponentType<PressableWebProps>;

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'text'
    | 'inverse'
    | 'destructivePrimary'
    | 'destructiveSecondary'
    | 'destructiveTertiary'
    | 'destructiveText'
    | 'utility';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
    testID?: string;

    /**
     * Change the style type of the button. Choose between `primary`, `secondary`, `tertiary`, `text`, `utility` and `inverse`, or destructive variants `destructivePrimary`, `destructiveSecondary`, `destructiveTertiary`, and `destructiveText`.
     * This can also be an array of sizes for responsive layouts.
     */
    variant?: ButtonVariant;

    /**
     * The content of the button
     */
    children?: React.ReactNode;

    /**
     * The callback for when the button is pressed
     */
    onPress: () => void;

    /**
     * Callback for when the button receives keyboard focus
     */
    onFocus?: () => void;

    /**
     * Callback for when the button loses keyboard focus
     */
    onBlur?: () => void;

    /**
     * Adds an icon to appear after (to the right of) the link text.
     */
    iconAfter?: ReactNode;

    /**
     * Adds an icon to appear before (to the left of) the button text.
     */
    iconBefore?: ReactNode;

    /**
     * Set to true to set styling for buttons with only an icon as the child.
     * Only use with `primary`, `secondary`, `tertiary`, `utility`, and `inverse` variants.
     */
    iconOnly?: boolean;

    /**
     * Whether the content for the button is loading
     * the press action cannot be triggered when this is set to true.
     */
    loading?: boolean;

    /**
     * Disables button interaction and applies disabled state styles.
     */
    disabled?: boolean;

    /**
     * Provides an accessible label for the button. This is especially important for icon-only buttons
     * to ensure they have a meaningful description for screen reader users.
     */
    ariaLabel?: string;

    /**
     * Size of the button. Use the sizings of `sm`, `md`, and `lg`.
     */
    size?: ButtonSize;

    /**
     * Defines the margin/padding style overrides.
     */
    sx?: StrictSpacingStyle;

    /**
     * Accessibility role for the button (e.g., 'radio' for radio buttons)
     */
    accessibilityRole?: AccessibilityRole;
    /**
     * Accessibility state for the button (e.g., { selected: true } for radio buttons)
     */
    accessibilityState?: AccessibilityState;

    ref?: React.Ref<View>;

    /**
     * [web] If present, the Button will be rendered using an anchor tag
     * See https://necolas.github.io/react-native-web/docs/accessibility/#links
     */
    href?: string;

    /**
     * [web] If `href` is present, specifies attributes for the anchor tag that is rendered
     * See https://necolas.github.io/react-native-web/docs/accessibility/#links
     */
    hrefAttrs?: {
        download?: boolean;
        rel?: string;
        target?: string;
    };

    /**
     * When true (default), button maintains its maximum measured width to prevent
     * layout shift during loading states or when content changes.
     * Set to false to allow the button to shrink when content width decreases.
     */
    maintainWidth?: boolean;

    /**
     * [native] When true, a focus ring is drawn around the button while it has
     * keyboard focus. No-op on web, where the browser's native focus handling
     * and react-native-web's `outline*` focus ring already apply. Opt-in to
     * avoid changing focus behavior on screens that haven't been tested with
     * hardware keyboards.
     */
    showNativeFocusRing?: boolean;
}

export function Button({
    testID,
    ariaLabel,
    variant = 'primary',
    children,
    onPress,
    onFocus,
    onBlur,
    iconBefore = null,
    iconAfter,
    loading = false,
    disabled = false,
    iconOnly = false,
    size = 'md',
    sx,
    accessibilityRole,
    accessibilityState,
    ref,
    href,
    hrefAttrs,
    maintainWidth = true,
    showNativeFocusRing = false,
}: ButtonProps) {
    const theme = useTheme();

    const [isActive, setIsActive] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocusedNative, setIsFocusedNative] = useState(false);
    const { isFocusVisible, focusProps } = useFocusRing();
    const [textWidth, setTextWidth] = useState(0);
    const [initialRender, setInitialRender] = useState(true);
    const hasMeasuredText = useRef(false);

    const variantStyles = useMemo(
        () => ({
            primary: { backgroundColor: theme.colors.backgroundActionPrimary },
            secondary: {
                backgroundColor: theme.colors.backgroundAction,
                borderColor: theme.colors.borderActionWeak,
                borderStyle: 'solid' as const,
                borderWidth: 1,
            },
            tertiary: { backgroundColor: 'transparent' },
            text: { backgroundColor: 'transparent' },
            inverse: {
                backgroundColor: theme.colors.backgroundActionTransparent,
                borderColor: theme.colors.borderInverse,
                borderStyle: 'solid' as const,
                borderWidth: 1,
            },
            destructivePrimary: { backgroundColor: theme.colors.backgroundActionDestructive },
            destructiveSecondary: {
                backgroundColor: '#FFFFFF', // TODO - Figma file is referencing tokens removed in IFL7
                borderColor: '#D4D2D0', // TODO - Figma file is referencing tokens removed in IFL7
                borderStyle: 'solid' as const,
                borderWidth: 1,
            },
            destructiveTertiary: { backgroundColor: 'transparent' },
            destructiveText: { backgroundColor: 'transparent' },
            utility: {
                backgroundColor: theme.colors.backgroundActionWeak,
            },
        }),
        [theme.colors],
    );

    const colors = useMemo(
        () => ({
            primary: { color: theme.colors.foregroundInverse },
            secondary: { color: theme.colors.foregroundActionPrimary },
            tertiary: { color: theme.colors.foregroundActionPrimary },
            text: { color: theme.colors.foregroundActionPrimary },
            inverse: { color: theme.colors.foregroundInverse },
            destructivePrimary: { color: theme.colors.foregroundInverse },
            destructiveSecondary: { color: theme.colors.foregroundActionDestructive },
            destructiveTertiary: { color: theme.colors.foregroundActionDestructive },
            destructiveText: { color: theme.colors.foregroundActionDestructive },
            utility: { color: theme.colors.foregroundStrong },
        }),
        [theme.colors],
    );

    const activeStyles = useMemo(
        () => ({
            primary: {
                backgroundColor: theme.colors.backgroundActionPrimaryActive,
            },
            secondary: {
                backgroundColor: theme.colors.backgroundActionActive,
                borderColor: theme.colors.borderActionActive,
            },
            tertiary: {
                backgroundColor: theme.colors.backgroundActionActive,
            },
            text: {},
            inverse: {
                backgroundColor: theme.colors.backgroundActionTransparentActive,
                borderColor: theme.colors.borderInverse,
            },
            destructivePrimary: {
                backgroundColor: theme.colors.backgroundActionDestructiveActive,
            },
            destructiveSecondary: {
                backgroundColor: '#F9C5C6', // TODO - Figma file is referencing tokens removed in IFL7
                borderColor: '#83161B', // TODO: Figma file is referencing tokens removed in IFL7
            },
            destructiveTertiary: {
                backgroundColor: theme.colors.backgroundActionActive,
            },
            destructiveText: {},
            utility: {
                backgroundColor: theme.colors.backgroundActionWeakActive,
            },
        }),
        [theme.colors],
    );

    const hoverStyles = useMemo(
        () => ({
            primary: {
                backgroundColor: theme.colors.backgroundActionPrimaryHover,
            },
            secondary: {
                backgroundColor: theme.colors.backgroundActionHover,
            },
            tertiary: {
                backgroundColor: theme.colors.backgroundActionHover,
            },
            text: {},
            inverse: {
                backgroundColor: theme.colors.backgroundActionTransparentHover,
            },
            destructivePrimary: {
                backgroundColor: theme.colors.backgroundActionDestructiveHover,
            },
            destructiveSecondary: {
                backgroundColor: theme.colors.backgroundActionHover,
            },
            destructiveTertiary: {
                backgroundColor: theme.colors.backgroundActionHover,
            },
            destructiveText: {},
            utility: {
                backgroundColor: theme.colors.backgroundActionWeakHover,
            },
        }),
        [theme.colors],
    );

    const activeTextColors = useMemo(
        () => ({
            primary: { color: theme.colors.foregroundInverse },
            secondary: { color: theme.colors.foregroundActionPrimaryActive },
            tertiary: { color: theme.colors.foregroundActionPrimaryActive },
            text: { color: theme.colors.foregroundActionPrimaryActive },
            inverse: { color: theme.colors.foregroundInverse },
            destructivePrimary: { color: theme.colors.foregroundInverse },
            destructiveSecondary: { color: theme.colors.foregroundActionDestructiveActive },
            destructiveTertiary: { color: theme.colors.foregroundActionDestructiveActive },
            destructiveText: { color: theme.colors.foregroundActionDestructiveActive },
            utility: { color: theme.colors.foregroundStrong },
        }),
        [theme.colors],
    );

    const hoverTextColors: Partial<Record<ButtonVariant, { color: string }>> = useMemo(
        () => ({
            text: { color: theme.colors.foregroundActionPrimaryHover },
            destructiveText: { color: theme.colors.foregroundActionDestructiveHover },
        }),
        [theme.colors],
    );

    const paddingSizes = useMemo(
        () => ({
            sm: theme.spacing.sc2,
            md: theme.spacing.sc3,
            lg: theme.spacing.sc4,
        }),
        [theme.spacing],
    );

    const dimensions = useMemo(
        () => ({
            sm: theme.spacing.s2,
            md: theme.spacing.s3,
            lg: theme.spacing.s4,
        }),
        [theme.spacing],
    );

    const styles = useMemo(
        () => ({
            base: {
                borderRadius: theme.borderRadius.md,
                paddingTop: theme.spacing.sc4,
                paddingBottom: theme.spacing.sc4,
                paddingLeft: theme.spacing.scb4,
                paddingRight: theme.spacing.scb4,
                alignItems: 'center' as const,
                justifyContent: 'center' as const,
                flexDirection: 'row' as const,
            },
            disabled: {
                opacity: 0.4,
                pointerEvents: 'none' as const,
            },
            hiddenMeasureContainer: {
                position: 'absolute' as const,
                opacity: 0,
                width: 0,
                height: 0,
                overflow: 'hidden' as const,
            },
            iconBeforeContainer: {
                paddingRight: iconOnly ? 0 : theme.spacing.s2,
            },
        }),
        [theme.borderRadius, theme.spacing, iconOnly],
    );

    const getSizeStyles = useCallback(
        (size: ButtonSize, iconOnly: boolean): ViewStyle => {
            return {
                paddingTop: paddingSizes[size],
                paddingBottom: paddingSizes[size],
                paddingLeft: iconOnly ? paddingSizes[size] : theme.spacing.s4, // TODO
                paddingRight: iconOnly ? paddingSizes[size] : theme.spacing.s4, // TODO
                minWidth: iconOnly ? dimensions[size] : undefined,
                minHeight: dimensions[size],
            };
        },
        [paddingSizes, dimensions, theme.spacing],
    );

    useEffect(() => {
        setInitialRender(false);
    }, []);

    const getStyle = useCallback(
        ({ hovered }: { pressed: boolean; hovered?: boolean }) => ({
            ...styles.base,
            ...variantStyles[variant],
            ...getSizeStyles(size, iconOnly),
            ...(disabled ? { opacity: 0.4 } : {}),
            ...(hovered && !isActive ? hoverStyles[variant] : {}),
            ...(isActive ? activeStyles[variant] : {}),
            ...sx,
            ...(Platform.OS === 'web' && isFocusVisible
                ? {
                      outlineStyle: 'solid' as const,
                      outlineWidth: 2,
                      outlineColor: theme.colors.utilityFocus,
                      outlineOffset: 2,
                  }
                : {}),
        }),
        [
            variant,
            disabled,
            size,
            iconOnly,
            isActive,
            isFocusVisible,
            styles.base,
            variantStyles,
            getSizeStyles,
            hoverStyles,
            activeStyles,
            sx,
            theme.colors.utilityFocus,
        ],
    );

    // Hardware-keyboard focus ring on native. RN renders the web-native
    // `outline*` focus ring on web only; on iOS/Android we track focus locally
    // and draw an absolute-positioned border as a child of the Pressable so
    // hardware-keyboard (TAB) users can see which button has focus. Negative
    // insets + `pointerEvents="none"` mean the ring draws outside the
    // Pressable without affecting layout, hit-testing, or parent flex metrics.
    // Keeping the ring inside the Pressable (rather than wrapping the whole
    // component) avoids remounting the Pressable when focus toggles, which on
    // Android would instantly drop the focus we just received.
    const nativeFocusRing =
        showNativeFocusRing && Platform.OS !== 'web' && isFocusedNative
            ? {
                  position: 'absolute' as const,
                  top: -3,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  borderRadius: theme.borderRadius.md + 3,
                  borderWidth: 2,
                  borderColor: theme.colors.utilityFocus,
              }
            : null;

    const hasIcon = !!iconAfter;

    const textStyle: StyleProp<TextStyle> = useMemo(
        () => [
            isActive
                ? activeTextColors[variant]
                : isHovered && hoverTextColors[variant]
                  ? hoverTextColors[variant]
                  : colors[variant],
            { fontWeight: String(theme.fontWeights.bold) as TextStyle['fontWeight'] },
            { fontFamily: theme.fonts.default },
            { fontSize: theme.fontSizes.f3 },
            {
                lineHeight: Math.round(theme.fontSizes.f3 * theme.lineHeights.normal),
            },
            { marginRight: hasIcon ? theme.spacing.s2 : 0 },
        ],
        [variant, hasIcon, isActive, isHovered, activeTextColors, hoverTextColors, colors, theme],
    );

    const press = useCallback(() => {
        if (disabled) {
            return;
        }

        onPress();
    }, [onPress, disabled]);

    const isPrimaryOrIsDestructive = variant === 'primary' || variant === 'destructivePrimary';

    const onTextLayout = useCallback(
        (e: { nativeEvent: { layout: { width: number } } }) => {
            const newWidth = e.nativeEvent.layout.width;
            if (maintainWidth) {
                if (newWidth > textWidth) {
                    setTextWidth(newWidth);
                    hasMeasuredText.current = true;
                }
            } else {
                if (newWidth !== textWidth) {
                    setTextWidth(newWidth);
                    hasMeasuredText.current = true;
                }
            }
        },
        [textWidth, maintainWidth],
    );

    const showSpinner = loading && !initialRender;

    return (
        <Pressable
            ref={ref as React.RefObject<View>}
            testID={testID}
            style={getStyle}
            onPress={press}
            onFocus={() => {
                focusProps.onFocus?.();
                if (showNativeFocusRing && Platform.OS !== 'web') setIsFocusedNative(true);
                onFocus?.();
            }}
            onBlur={() => {
                focusProps.onBlur?.();
                if (showNativeFocusRing && Platform.OS !== 'web') setIsFocusedNative(false);
                onBlur?.();
            }}
            disabled={loading || disabled}
            accessible={true}
            aria-label={ariaLabel}
            accessibilityLabel={ariaLabel}
            accessibilityRole={accessibilityRole ?? 'button'}
            role={Platform.OS === 'web' && href ? 'link' : 'button'}
            accessibilityState={{
                disabled: disabled,
                busy: loading,
                ...accessibilityState,
            }}
            onPressIn={() => setIsActive(true)}
            onPressOut={() => setIsActive(false)}
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
            href={Platform.OS === 'web' ? href : undefined}
            hrefAttrs={Platform.OS === 'web' && href ? hrefAttrs : undefined}
        >
            {iconBefore && (
                <Box
                    sx={styles.iconBeforeContainer}
                    pointerEvents="none"
                    focusable={false}
                    aria-hidden={true}
                >
                    {iconBefore}
                </Box>
            )}

            {!iconOnly && (
                <Box
                    sx={{
                        minWidth: maintainWidth && textWidth > 0 ? textWidth : undefined,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {showSpinner ? (
                        <Spinner inverse={isPrimaryOrIsDestructive} />
                    ) : (
                        <View onLayout={onTextLayout}>
                            <Text sx={textStyle}>{children}</Text>
                        </View>
                    )}
                    {initialRender && (
                        <View style={styles.hiddenMeasureContainer} onLayout={onTextLayout}>
                            <Text sx={textStyle}>{children}</Text>
                        </View>
                    )}
                </Box>
            )}
            {hasIcon && (
                <Box pointerEvents="none" focusable={false} aria-hidden={true}>
                    {iconAfter}
                </Box>
            )}
            {nativeFocusRing ? (
                <View style={nativeFocusRing} pointerEvents="none" aria-hidden={true} />
            ) : null}
        </Pressable>
    );
}
